/**
 * Actions API service (G5.6 — visible outcomes for approved actions).
 *
 * Backed by /api/actions: list executed/failed actions, read the execution
 * log, and roll back reversible succeeded actions.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface ActionTargetInfo {
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
}

export interface ActionSummary {
  action_id: string;
  action_type: string;
  status: string;
  platform: string;
  target: ActionTargetInfo;
  action_params: Record<string, unknown>;
  approved_by: string | null;
  approved_at: string | null;
  execution_started_at: string | null;
  execution_completed_at: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  can_rollback: boolean;
  rollback_executed_at: string | null;
  error_message: string | null;
  error_code: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface ActionsListResponse {
  actions: ActionSummary[];
  total: number;
  has_more: boolean;
}

export interface ExecutionLogEntry {
  id: string;
  event_type: string;
  event_timestamp: string;
  http_status_code: number | null;
  error_details: Record<string, unknown> | null;
  triggered_by: string | null;
}

export interface ExecutionLogsResponse {
  action_id: string;
  entries: ExecutionLogEntry[];
}

export interface RollbackResponse {
  status: string;
  action_id: string;
  new_status: string;
  message: string;
}

export async function fetchActions(
  limit = 50,
  offset = 0
): Promise<ActionsListResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/actions?limit=${limit}&offset=${offset}`,
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<ActionsListResponse>(response);
}

export async function fetchExecutionLogs(
  actionId: string
): Promise<ExecutionLogsResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/actions/${actionId}/logs`,
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<ExecutionLogsResponse>(response);
}

export async function rollbackAction(
  actionId: string,
  reason?: string
): Promise<RollbackResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/actions/${actionId}/rollback`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(reason ? { reason } : {}),
    }
  );
  return handleResponse<RollbackResponse>(response);
}
